/* Copyright (c) 2018 Backpack Health LLC */
import React from 'react';
import _ from 'lodash';

import { eSurveyQuestionCategoryName } from '../../../../apiServices/SurveyService';

import SingleAnswerQuestion, { NestedSingleAnswerQuestion, SingleAnswerAnswer, CompositeSingleAnswerAnswer } from './SingleAnswerQuestion';
import FuzzyDateQuestion, { NestedFuzzyDateQuestion, FuzzyDateAnswer } from './FuzzyDateQuestion';
import { RapidResponseQuestionView, RapidResponseAnswer } from './RapidResponseQuestion';
import AmountEntryQuestion, { AmountEntryAnswer } from './AmountEntryQuestion';
import TextEntryQuestion, { TextEntryAnswer } from './TextEntryQuestion';
import CompositeQuestion from './CompositeQuestion';

export const RenderSurveyAnswers = (props) => {
  const { answer, question } = props;

  switch (answer.category) {
    case eSurveyQuestionCategoryName.singleAnswer:
      return <SingleAnswerAnswer {...props} />;
    case eSurveyQuestionCategoryName.fuzzyDate:
      return <FuzzyDateAnswer {...props} />;
    case eSurveyQuestionCategoryName.rapidResponse:
      return <RapidResponseAnswer {...props} />;
    case eSurveyQuestionCategoryName.amountEntry:
      return <AmountEntryAnswer {...props} />;
    case eSurveyQuestionCategoryName.textEntry:
      return <TextEntryAnswer {...props} />;
    case eSurveyQuestionCategoryName.composite:
      return (
        <>
          {answer[answer.category].segments.map((c, i) => {
            switch (c.category) {
              case eSurveyQuestionCategoryName.singleAnswer:
                return <CompositeSingleAnswerAnswer key={i} {...props} answer={c} question={question[question.category].segments[i]} isLast={i >= answer[answer.category].segments.length - 1} />;
              default:
                return <RenderSurveyAnswers key={i} {...props} answer={c} question={question[question.category].segments[i]} />;
            }
          })}
        </>
      );
    default:
      return null;
  }
};

export const RenderNestedSurveyQuestion = (props) => {
  const { question } = props;

  switch (question.category) {
    case eSurveyQuestionCategoryName.singleAnswer:
      return <NestedSingleAnswerQuestion {...props} />;
    case eSurveyQuestionCategoryName.fuzzyDate:
      return <NestedFuzzyDateQuestion {...props} />;
    case eSurveyQuestionCategoryName.rapidResponse:
      return <RapidResponseQuestionView {...props} />;
    case eSurveyQuestionCategoryName.amountEntry:
      return <AmountEntryQuestion {...props} />;
    case eSurveyQuestionCategoryName.textEntry:
      return <TextEntryQuestion {...props} />;
    default:
      return null;
  }
};

interface RenderSurveyQuestionProps {
  question: {
    category: eSurveyQuestionCategoryName;
    id: number;
  };
  response: any;
  profileId: number;
  showValidationError: boolean;
  clearError: Function;
  onUpdate: (data: any) => void;
  onSubmit: () => void;
}

const RenderSurveyQuestionRef = (props: RenderSurveyQuestionProps, ref) => {
  const { question } = props;
  const questionComponentRef = React.createRef();

  React.useImperativeHandle(ref, () => {
    return {
      onDisabledClick: () => {
        const handler = _.get(questionComponentRef, 'current.onDisabledClick', null);
        if (handler) {
          handler();
        }
      },
    };
  });

  switch (question.category) {
    case eSurveyQuestionCategoryName.rapidResponse:
      return (
        <RapidResponseQuestionView
          key={question.id}
          {...props}
          ref={questionComponentRef}
          hasLastItem={true}
          showHint={true}
          idPrefix="rapid-response-row-"
          questionClassSelector="survey-question"
        />
      );
    case eSurveyQuestionCategoryName.composite:
      return (
        <CompositeQuestion
          key={question.id}
          {...props}
          ref={questionComponentRef}
        />
      );
    case eSurveyQuestionCategoryName.singleAnswer:
      return <SingleAnswerQuestion key={question.id} {...props} />;
    case eSurveyQuestionCategoryName.fuzzyDate:
      return <FuzzyDateQuestion key={question.id} {...props} />;
    case eSurveyQuestionCategoryName.amountEntry:
      return <AmountEntryQuestion key={question.id} {...props} />;
    case eSurveyQuestionCategoryName.textEntry:
      return <TextEntryQuestion key={question.id} {...props} />;
    default:
      return null;
  }
};

const RenderSurveyQuestion = React.forwardRef(RenderSurveyQuestionRef);

export default RenderSurveyQuestion;
